import { s } from '@/theme/tokens';
import { detailPageSectionGap } from '@/ui/composites/scaffolds/detailPageSpacing';
import { useMediaQuery, useTheme } from '@/ui/primitives/hooks';
import { metricTrendCardCompactLayout } from './MetricTrendCardFrame.layout';

export type StackedMetricsStatusChartPanelLayout = {
  height: number;
  chartHeight: number;
  headerHeight: number;
  headerToChartGap: number;
  panelPadding: 'md';
  panelContentGap: 'md';
  headerTitlePaddingLeft: string;
  headerControlsPaddingRight: string;
  yAxisWidth: number;
  yAxisAutoWidth: boolean;
  yAxisAutoWidthPadding: number;
  axisStrokeWidth: number;
  barCornerRadius: number;
  categoryGapRatio: number;
  barGapRatio: number;
  yAxisLabelOffset: number;
  hideXAxisTicks: boolean;
  xAxisLabelOffset: number;
  chartHostPaddingX: string;
  chartHostPaddingTop: string;
  chartHostPaddingBottom: string;
  chartMargins: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
};

export type StackedMetricsMiniPanelsLayout = {
  columnHeight: number;
  height: number;
  headerHeight: number;
  chartHeight: number;
  columnGap: string;
  rowGap: string;
  rowGapPx: number;
  panelPaddingTop: string;
  panelPaddingBottom: string;
  headerPaddingX: string;
  headerPaddingTop: string;
  headerPaddingBottom: string;
  contentPaddingX: string;
  contentPaddingTop: string;
  contentPaddingBottom: string;
  chartWrapperPaddingX: string;
  chartWrapperPaddingTop: string;
  chartWrapperPaddingBottom: string;
  yAxisWidth: number;
  axisStrokeWidth: number;
  yDomainPaddingTopRatio: number;
  yDomainPaddingBottomRatio: number;
  chartMargins: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
};

export type StackedMetricsSectionLayout = {
  gap: string;
  rowGap: string;
  toolbarGap: string;
  bottomGap: string;
};

export type StackedMetricsStatusChartSectionLayout = {
  isNarrowChartLayout: boolean;
  isTwoColumnChartLayout: boolean;
  piePanelHeight: number;
  pieChartVisualHeight: number;
  statusChartVisualHeight: number;
  statusPanel: StackedMetricsStatusChartPanelLayout;
  miniPanels: StackedMetricsMiniPanelsLayout;
  sectionLayout: StackedMetricsSectionLayout;
};

export const useStackedMetricsStatusChartSectionLayout =
  (): StackedMetricsStatusChartSectionLayout => {
    const theme = useTheme();
    const isNarrowChartLayout = useMediaQuery(theme.breakpoints.down('lg'));
    const isTwoColumnChartLayout = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
    const miniPanelCount = 4;
    const chartComponentGap = s('xl');
    const chartColumnGap = s('2xl');
    const metricsColumnHeight =
      metricTrendCardCompactLayout.minHeight * miniPanelCount +
      metricTrendCardCompactLayout.rowGapPx * (miniPanelCount - 1);
    const sharedTwoColumnPanelHeight = 288;
    const miniPanelsColumnHeight = isTwoColumnChartLayout
      ? sharedTwoColumnPanelHeight
      : metricsColumnHeight;
    const miniPanelsRowGapPx = metricTrendCardCompactLayout.rowGapPx;
    const stackedSectionBaseRowHeight =
      (miniPanelsColumnHeight - miniPanelsRowGapPx * (miniPanelCount - 1)) / miniPanelCount;
    const piePanelHeight = isTwoColumnChartLayout
      ? sharedTwoColumnPanelHeight
      : isNarrowChartLayout
        ? 340
        : metricsColumnHeight;

    const statusPanel: StackedMetricsStatusChartPanelLayout = {
      height: isTwoColumnChartLayout
        ? sharedTwoColumnPanelHeight
        : isNarrowChartLayout
          ? 260
          : metricsColumnHeight,
      chartHeight: isTwoColumnChartLayout
        ? 216
        : isNarrowChartLayout
          ? 172
          : metricsColumnHeight - 42,
      headerHeight: 28,
      headerToChartGap: isNarrowChartLayout ? 10 : 12,
      panelPadding: 'md',
      panelContentGap: 'md',
      headerTitlePaddingLeft: s('md'),
      headerControlsPaddingRight: s('md'),
      yAxisWidth: 48,
      yAxisAutoWidth: true,
      yAxisAutoWidthPadding: 8,
      axisStrokeWidth: 0.75,
      barCornerRadius: 1,
      categoryGapRatio: 0.68,
      barGapRatio: 0.5,
      yAxisLabelOffset: -2,
      hideXAxisTicks: false,
      xAxisLabelOffset: 3,
      chartHostPaddingX: s('xl'),
      chartHostPaddingTop: s('xs'),
      chartHostPaddingBottom: s('sm'),
      chartMargins: {
        left: -8,
        right: 32,
        top: 0,
        bottom: isNarrowChartLayout ? 10 : 14,
      },
    };

    const miniPanels: StackedMetricsMiniPanelsLayout = {
      columnHeight: miniPanelsColumnHeight,
      height: stackedSectionBaseRowHeight,
      headerHeight: 24,
      chartHeight: 26,
      columnGap: chartComponentGap,
      rowGap: chartComponentGap,
      rowGapPx: miniPanelsRowGapPx,
      panelPaddingTop: s('sm'),
      panelPaddingBottom: s('xs'),
      headerPaddingX: s('xs'),
      headerPaddingTop: s('xs'),
      headerPaddingBottom: s('xs'),
      contentPaddingX: s('none'),
      contentPaddingTop: s('none'),
      contentPaddingBottom: s('none'),
      chartWrapperPaddingX: s('none'),
      chartWrapperPaddingTop: s('xxs'),
      chartWrapperPaddingBottom: s('xxs'),
      yAxisWidth: 0,
      axisStrokeWidth: 0.75,
      yDomainPaddingTopRatio: 0.16,
      yDomainPaddingBottomRatio: 0.05,
      chartMargins: {
        left: 0,
        right: 0,
        top: 5,
        bottom: -4,
      },
    };

    const sectionLayout: StackedMetricsSectionLayout = {
      gap: chartColumnGap,
      rowGap: chartComponentGap,
      toolbarGap: detailPageSectionGap,
      bottomGap: s('none'),
    };

    const pieChartVisualHeight = isNarrowChartLayout
      ? piePanelHeight - miniPanels.headerHeight - (isTwoColumnChartLayout ? 60 : 36)
      : piePanelHeight - miniPanels.headerHeight - 56;
    const statusChartVisualHeight = statusPanel.chartHeight;

    return {
      isNarrowChartLayout,
      isTwoColumnChartLayout,
      piePanelHeight,
      pieChartVisualHeight,
      statusChartVisualHeight,
      statusPanel,
      miniPanels,
      sectionLayout,
    };
  };
